import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaArrowRight } from 'react-icons/fa';
import Seo from '@/components/seo/Seo';
import { content } from '@/content/sr';
import { CONTACT_FORM_PATH } from '@/constants/paths';
import { breadcrumbJsonLd, canonicalUrl } from '@/config/seo';

const ProductsPage = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
    },
  };

  const pageTitle = 'Proizvodi — paneli, pergole, staklo, nameštaj po meri i kamen';
  const pageDescription = `${content.products.subtitle} Uvoz i prodaja u Užicu, isporuka širom Srbije.`;

  // Lista kategorija kao ItemList — Google je koristi za sitelinkove ka kategorijama.
  const categoryList = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Ponuda Wall Lux Trade',
    itemListElement: content.products.categories.map((category, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: category.name,
      description: category.description,
      url: `${canonicalUrl(`/shop/${category.id}`)}`,
    })),
  };

  return (
    <>
      <Seo
        title={pageTitle}
        description={pageDescription}
        path="/proizvodi"
        jsonLd={[breadcrumbJsonLd([{ name: 'Proizvodi', path: '/proizvodi' }]), categoryList]}
      />

      <div data-testid="products-page">
        {/* Hero — isti vizuelni jezik kao „O nama“ (slika + overlay) */}
        <section className="relative py-32 md:py-40 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920"
              alt="Proizvodi"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-brand-primary/80" />
          </div>
          <div className="container-custom relative z-10 text-center">
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="caption text-brand-accent block mb-4"
            >
              {content.products.caption}
            </motion.span>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="font-heading text-5xl md:text-7xl font-light text-white mb-4"
            >
              {content.products.title}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-neutral-200 text-lg max-w-2xl mx-auto"
            >
              {content.products.subtitle}
            </motion.p>
          </div>
        </section>

        {/* Products Grid */}
        <section className="section-padding bg-white">
          <div className="container-custom">
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            >
              {content.products.categories.map((category) => (
                <motion.div
                  key={category.id}
                  variants={itemVariants}
                  data-testid={`product-category-${category.id}`}
                >
                  <Link
                    to={`/shop/${category.id}`}
                    className="group block focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2"
                  >
                    <div className="relative overflow-hidden mb-6">
                      <div className="aspect-[4/5]">
                        <img
                          src={category.image}
                          alt={category.name}
                          className="w-full h-full object-cover transition-transform duration-700 
                                   group-hover:scale-105"
                        />
                      </div>
                      <div className="absolute inset-0 bg-brand-primary/0 group-hover:bg-brand-primary/20 
                                    transition-colors duration-500" />
                    </div>
                    <h3 className="font-heading text-2xl text-brand-primary mb-2">
                      {category.name}
                    </h3>
                    <p className="text-neutral-500 mb-4">{category.description}</p>
                    <span className="inline-flex items-center gap-2 text-brand-accent font-bold text-sm 
                             group-hover:text-brand-accentHover transition-colors">
                      Pogledajte ponudu <FaArrowRight size={12} />
                    </span>
                  </Link>
                </motion.div>
              ))}
            </motion.div>

            <div className="text-center mt-12">
              <p className="text-sm text-neutral-500 mb-4 max-w-2xl mx-auto">
                {content.products.shopHint}
              </p>
              <Link to="/shop" className="btn-accent inline-block" data-testid="products-to-shop">
                Uđite u prodavnicu
              </Link>
            </div>

            {/* Info Box */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="mt-20 p-10 bg-brand-bgSoft text-center"
            >
              <h3 className="font-heading text-2xl text-brand-primary mb-4">
                Potrebna Vam Je Pomoć Pri Izboru?
              </h3>
              <p className="text-neutral-700 mb-6 max-w-2xl mx-auto">
                Naš tim stručnjaka je tu da vam pomogne da pronađete savršeno rešenje za vaš prostor.
                Kontaktirajte nas za besplatne konsultacije.
              </p>
              <Link to={CONTACT_FORM_PATH} className="btn-accent inline-block">
                Kontaktirajte Nas
              </Link>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ProductsPage;
